// Types only: the catalog's feature names are gated, and the public help site
// bundles this module (via LoadingPage), so its data must not ship there.
import type { FeatureId } from "@brev-io/common/src/analytics/feature-catalog";
import { ANALYTICS_EVENTS, type AnalyticsEvent } from "./events";

const E = ANALYTICS_EVENTS;

/**
 * BRE-9866: the product feature each client event counts toward, stamped as
 * `feature_id` and `feature_area` by `captureProductClientEvent`. `null` is
 * for events that are not product use: performance, error and sync
 * telemetry, and events no feature owns. A caller that knows better passes
 * its own `feature_id`, which wins (sign-up reuses sign-in's events).
 */
export const EVENT_FEATURES: Readonly<Record<AnalyticsEvent, FeatureId | null>> = Object.freeze({
  // ── Auth ──
  [E.AUTH_SESSION_START]: "auth.session",
  [E.AUTH_LOGIN]: "auth.sign-in",
  [E.AUTH_LOGIN_FAILED]: "auth.sign-in",
  [E.AUTH_LOGOUT]: "auth.session",
  [E.AUTH_SIGNUP]: "auth.sign-up",
  [E.AUTH_SIGNUP_FAILED]: "auth.sign-up",
  [E.AUTH_SIGNUP_EXISTING_ACCOUNT]: "auth.sign-up",
  [E.AUTH_SIGNUP_COMPLETED]: "auth.sign-up",
  [E.AUTH_MAGIC_CODE_SENT]: "auth.sign-in",
  [E.AUTH_MAGIC_CODE_FAILED]: "auth.sign-in",
  [E.AUTH_UNDECIDED]: null,
  [E.AUTH_SSO_REDIRECT]: "auth.sign-in",
  [E.AUTH_GOOGLE_OAUTH_CLICKED]: "auth.sign-in",
  [E.AUTH_MICROSOFT_OAUTH_CLICKED]: "auth.sign-in",
  [E.AUTH_SLACK_OAUTH_CLICKED]: "auth.sign-in",
  [E.AUTH_IN_APP_BROWSER_DETECTED]: "auth.sign-in",
  [E.AUTH_IN_APP_BROWSER_ESCAPE_CLICKED]: "auth.sign-in",
  [E.AUTH_MFA_CHALLENGED]: "auth.sign-in",
  [E.AUTH_MFA_VERIFIED]: "auth.sign-in",
  [E.AUTH_MFA_FAILED]: "auth.sign-in",
  [E.AUTH_EMAIL_VERIFICATION_REQUIRED]: "auth.sign-in",
  [E.AUTH_EMAIL_VERIFIED]: "auth.sign-in",
  [E.AUTH_EMAIL_VERIFY_FAILED]: "auth.sign-in",
  [E.AUTH_REDIRECT]: "auth.session",

  // ── Onboarding ──
  [E.ONBOARDING_STARTED]: "onboarding.flow",
  [E.ONBOARDING_STEP_VIEWED]: "onboarding.flow",
  [E.ONBOARDING_STEP_COMPLETED]: "onboarding.flow",
  [E.ONBOARDING_STEP_LOAD_TIME]: null,
  [E.ONBOARDING_COMPLETED]: "onboarding.flow",
  [E.ONBOARDING_ABANDONED]: "onboarding.flow",
  [E.ONBOARDING_BACK_CLICKED]: "onboarding.flow",
  [E.ONBOARDING_CONTINUE_CLICKED]: "onboarding.flow",
  [E.ONBOARDING_SAVE_FAILED]: "onboarding.flow",
  [E.ONBOARDING_ROLE_SELECTED]: "onboarding.profile",
  [E.ONBOARDING_COMPANY_SUBMITTED]: "onboarding.company",
  [E.ONBOARDING_MEETING_AGENT_CONFIGURED]: "personal-ai-settings.meeting-agent",
  [E.ONBOARDING_FIRST_GOAL_SAVED]: "onboarding.flow",
  [E.ONBOARDING_CONNECT_REMINDER_CLOSED]: "onboarding.flow",
  [E.ONBOARDING_GOAL_IMPORT_SHOWN]: "onboarding.flow",
  [E.ONBOARDING_GOAL_IMPORT_CLICKED]: "onboarding.flow",
  [E.ONBOARDING_GOAL_IMPORT_DISMISSED]: "onboarding.flow",
  [E.ONBOARDING_GOAL_IMPORT_COMPLETED]: "onboarding.flow",
  [E.ONBOARDING_OBJECTIVES_SUGGESTED]: "goals.ai-suggestion",
  [E.ONBOARDING_OBJECTIVES_STREAMING_PHASE]: null,
  [E.ONBOARDING_FIELD_SELECTED]: "onboarding.profile",
  [E.ONBOARDING_PROFILE_PICTURE_UPLOADED]: "onboarding.profile",
  [E.ONBOARDING_PROFILE_PICTURE_UPLOAD_FAILED]: "onboarding.profile",
  [E.ONBOARDING_PROFILE_PICTURE_REMOVED]: "onboarding.profile",
  [E.ONBOARDING_CALENDAR_CONNECT_CLICKED]: "onboarding.calendar",
  [E.ONBOARDING_CALENDAR_ALREADY_CONNECTED]: "onboarding.calendar",
  [E.ONBOARDING_CALENDAR_CONNECT_SUCCEEDED]: "onboarding.calendar",
  [E.ONBOARDING_CALENDAR_CONNECT_FAILED]: "onboarding.calendar",
  [E.ONBOARDING_CALENDAR_SKIPPED]: "onboarding.calendar",
  [E.ONBOARDING_WORKSPACE_CONNECT_CLICKED]: "onboarding.coworker",
  [E.ONBOARDING_WORKSPACE_ALREADY_CONNECTED]: "onboarding.coworker",
  [E.ONBOARDING_WORKSPACE_CONNECT_SUCCEEDED]: "onboarding.coworker",
  [E.ONBOARDING_WORKSPACE_CONNECT_FAILED]: "onboarding.coworker",
  [E.ONBOARDING_WORKSPACE_SKIPPED]: "onboarding.coworker",
  [E.AI_COWORKER_CONNECT_CLICKED]: "personal-ai-settings.slack-coworker",
  [E.ONBOARDING_FIRMOGRAPHICS_RECEIVED]: "onboarding.company",
  [E.ONBOARDING_FIRMOGRAPHICS_POLL_TIMEOUT]: null,
  [E.ONBOARDING_SLACK_FIRST_REDIRECTED]: "onboarding.flow",
  [E.ONBOARDING_SLACK_FIRST_RETURNED]: "onboarding.flow",
  [E.ONBOARDING_SLACK_FIRST_SKIPPED]: "onboarding.flow",
  [E.ONBOARDING_SLACK_FIRST_CONTINUE_IN_SLACK_CLICKED]: "onboarding.flow",
  [E.ONBOARDING_SLACK_FIRST_CONTINUE_ON_WEB_CLICKED]: "onboarding.flow",
  [E.ONBOARDING_ERROR_BOUNDARY_HIT]: null,
  [E.ONBOARDING_NAV_WATCHDOG_FIRED]: null,

  // ── Stale bundles, version skew, client errors ──
  [E.CHUNK_LOAD_ERROR]: null,
  [E.SERVER_ACTION_VERSION_SKEW]: null,
  [E.ELEMENT_TYPE_INVALID_ERROR]: null,
  [E.VERSION_UPDATE_TOAST_SHOWN]: null,
  [E.VERSION_UPDATE_UPDATE_CLICKED_PASSIVE]: null,
  [E.VERSION_UPDATE_UPDATE_CLICKED]: null,
  [E.VERSION_UPDATE_SOFT_REFRESH_SETTLED]: null,
  [E.VERSION_UPDATE_FALLBACK_RELOAD]: null,
  [E.FETCH_FAILED]: null,
  [E.CLIENT_ERROR_DIAGNOSTIC]: null,

  // ── Goal AI generation ──
  [E.GOAL_AI_GENERATION_STARTED]: "goals.ai-suggestion",
  [E.GOAL_AI_GENERATION_COMPLETED]: "goals.ai-suggestion",
  [E.GOAL_AI_GENERATION_FAILED]: "goals.ai-suggestion",
  [E.GOAL_METRIC_TITLE_MODEL_FILLED]: "goals.create",
  [E.GOAL_AI_GENERATION_ITEM_COMPLETED]: "goals.ai-suggestion",
  [E.GOAL_AI_SUGGESTION_ACCEPTED]: "goals.ai-suggestion",
  [E.GOAL_AI_SUGGESTION_DISMISSED]: "goals.ai-suggestion",

  // ── Goals ──
  [E.GOAL_CREATED]: "goals.create",
  [E.GOAL_UPDATED]: "goals.edit",
  [E.GOAL_DELETED]: "goals.delete",
  [E.GOAL_ARCHIVED]: "goals.archive",
  [E.GOAL_UNARCHIVED]: "goals.archive",
  [E.GOAL_STATUS_CHANGED]: "goals.edit",
  [E.GOAL_ACHIEVED]: "checkins.score-goal",
  [E.GOAL_IMPORT_STARTED]: "goals.create",
  [E.GOAL_IMPORT_COMPLETED]: "goals.create",
  [E.GOAL_REORDERED]: "goals.edit",

  // ── Check-ins and comments ──
  [E.CHECKIN_CREATED]: "checkins.create",
  [E.CHECKIN_DELETED]: "checkins.delete",
  [E.CHECKIN_EMAIL_CLICKED]: "checkins.create",
  [E.CHECKIN_STARTED]: "checkins.create",
  [E.CHECKIN_COMPLETED]: "checkins.create",
  [E.COMMENT_ADDED]: "checkins.comment",

  // ── Actions ──
  [E.ACTION_CREATED]: "actions.create",
  [E.ACTION_UPDATED]: "actions.edit",
  [E.ACTION_STATUS_CHANGED]: "actions.status",
  [E.ACTION_ASSIGNED]: "actions.edit",
  [E.ACTION_DELETED]: "actions.delete",
  [E.SUGGESTED_ACTION_SHOWN]: "actions.ai-suggestion",
  [E.SUGGESTED_ACTION_APPROVED]: "actions.ai-suggestion",
  [E.SUGGESTED_ACTION_DISMISSED]: "actions.ai-suggestion",

  // ── Tags, time periods, inbox, membership ──
  [E.TAG_CREATED]: "goals.edit",
  [E.TAG_UPDATED]: "goals.edit",
  [E.TAG_DELETED]: "goals.edit",
  [E.GOAL_TAGS_CHANGED]: "goals.edit",
  [E.TIME_PERIOD_CREATED]: "org-settings.time-periods",
  [E.TIME_PERIOD_UPDATED]: "org-settings.time-periods",
  [E.TIME_PERIOD_DELETED]: "org-settings.time-periods",
  [E.INBOX_ITEM_READ]: "inbox.triage",
  [E.INBOX_ALL_MARKED_READ]: "inbox.triage",
  [E.INBOX_ITEM_ARCHIVED]: "inbox.triage",
  [E.INBOX_BULK_ARCHIVED]: "inbox.triage",
  [E.ORG_MEMBER_ROLE_CHANGED]: "org-settings.members",
  [E.ORG_MEMBER_STATUS_CHANGED]: "org-settings.members",
  [E.ORG_MEMBER_INVITATION_SENT]: "org-settings.invite",
  [E.ORG_MEMBER_BULK_INVITATIONS_SENT]: "org-settings.invite",
  [E.ORG_INVITATION_DELETED]: "org-settings.invite",

  // ── Goal interactions ──
  [E.GOAL_LINK_COPIED]: "goals.view",
  [E.GOAL_KPI_CARD_TOGGLED]: "views.kpi-cards",
  [E.GOAL_PARENT_CHANGED]: "goals.align",
  [E.GOAL_OWNERS_CHANGED]: "goals.edit",
  [E.GOAL_KR_SUGGESTIONS_REQUESTED]: "goals.ai-suggestion",

  // ── Cadences and meetings ──
  [E.CADENCE_CREATED]: "cadences.configure",
  [E.CADENCE_DELETED]: "cadences.configure",
  [E.CADENCE_EVENT_VIEWED]: "cadences.view",
  [E.MEETING_NOTETAKER_TOGGLED]: "cadences.record",
  [E.MEETING_HOST_ONLY_TOGGLED]: "cadences.share",
  [E.MEETING_SUMMARY_COPIED]: "cadences.recap",
  [E.MEETING_SUMMARY_VIEWED]: "cadences.recap",
  [E.MEETING_TRANSCRIPT_COPIED]: "cadences.recap",
  [E.MEETING_TRANSCRIPT_ENTRY_COPIED]: "cadences.recap",
  [E.MEETING_REMINDER_RESOLVED]: "cadences.view",

  // ── Integrations ──
  [E.INTEGRATION_CONNECT_STARTED]: "integrations.connect",
  [E.INTEGRATION_CONNECTED]: "integrations.connect",
  [E.INTEGRATION_DISCONNECTED]: "integrations.manage",
  [E.INTEGRATION_CONNECTION_FAILED]: "integrations.connect",
  [E.GOAL_INTEGRATION_LINKED]: "goals.tracking",
  [E.INTEGRATION_DIALOG_DISMISSED]: "goals.tracking",

  // ── Teams and views ──
  [E.TEAM_CREATED]: "org-settings.teams",
  [E.TEAM_DELETED]: "org-settings.teams",
  [E.TEAM_MEMBER_ADDED]: "org-settings.teams",
  [E.CUSTOM_VIEW_CREATED]: "views.manage",
  [E.CUSTOM_VIEW_DELETED]: "views.manage",
  [E.CUSTOM_VIEW_RENAMED]: "views.manage",
  [E.CUSTOM_VIEW_FILTERS_UPDATED]: "views.filter",
  [E.VIEW_AUTOHEAL_ENSURE]: null,

  // ── Agent / AI ──
  [E.AGENT_MESSAGE_SENT]: "agent.chat",
  [E.OPERATOR_QUESTION_CARD_ANSWERED]: "agent.chat",
  [E.AGENT_CREATED]: null,
  [E.AGENT_RENAMED]: null,
  [E.AGENT_DELETED]: null,
  [E.AGENT_TOGGLED]: null,
  [E.OPERATOR_TURN_FIRST_RESPONSE]: null,
  [E.OPERATOR_TURN_COMPLETED]: null,
  [E.OPERATOR_TURN_CONTENT_PRESENTED]: null,
  [E.OPERATOR_TURN_PRESENTATION_DIAGNOSTIC]: null,
  [E.OPERATOR_TURN_STALE_SEND_REMINT]: null,
  // Feedback spans every AI surface; `content_type` says which.
  [E.AI_FEEDBACK_SUBMITTED]: null,
  [E.AI_SUMMARY_GENERATED]: null,
  [E.AI_SUMMARY_CLEARED]: null,
  [E.ARTIFACT_PREVIEW_SUMMARY_STUCK]: null,
  [E.WORKFLOW_STEP_SKIPPED]: null,
  [E.WORKFLOW_STEP_RETRIED]: null,
  [E.WORKFLOW_INVITATION_ACCEPTED]: null,
  [E.EDGE_REJECTION_PROBED]: null,

  // ── Performance and sync ──
  [E.LOADING_PAGE_SHOWN]: null,
  [E.LAYOUT_PREFETCH_DURATION]: null,
  [E.LAYOUT_PREFETCH_DISPATCHED]: null,
  [E.LAYOUT_RENDER_UNBLOCKED]: null,
  [E.INCREMENTAL_RENDER_DECISION]: null,
  [E.PERSONAL_GOALS_PAGE_SERVER_FETCH]: null,
  [E.ROUTE_INTERACTIVE]: null,
  [E.GOAL_TOGGLE_LATENCY]: null,
  [E.ARTIFACTS_GRID_FIRST_CONTENT]: null,
  [E.EVENT_ARTIFACTS_TAB_FIRST_CONTENT]: null,
  [E.ARTIFACT_RELAY_LATENCY_SAMPLED]: null,
  [E.QUERY_FAILED]: null,
  [E.SERVER_ACTION_BAD_RESPONSE]: null,
  [E.HEADER_OVERFLOW_SURFACED]: null,
  [E.AUTH_HYDRATION_MISMATCH_RECOVERED]: null,
  [E.PAGE_SLOW_LOAD]: null,
  [E.RXDB_REPLICATION_FAILED]: null,

  // ── Product tours ──
  [E.TOUR_STARTED]: "onboarding.flow",
  [E.TOUR_STEP_VIEWED]: "onboarding.flow",
  [E.TOUR_STEP_COMPLETED]: "onboarding.flow",
  [E.TOUR_COMPLETED]: "onboarding.flow",
  [E.TOUR_SKIPPED]: "onboarding.flow",
  [E.TOUR_STEP_SKIPPED]: "onboarding.flow",
  [E.TOUR_LAUNCH_DEFERRED]: "onboarding.flow",

  // ── Settings, invites, navigation ──
  [E.SETTINGS_CHANGED]: "org-settings.goal-settings",
  [E.FEATURE_REQUESTED]: "personal-ai-settings.feature-request",
  [E.INVITE_SENT]: "org-settings.invite",
  [E.INVITE_UI_SHOWN]: "org-settings.invite",
  [E.INVITE_UI_OPENED]: "org-settings.invite",
  [E.PAGE_VIEWED]: "shell.navigate",
  [E.MOBILE_LAYOUTS_ROUTE_VIEWED]: "shell.navigate",
  [E.COMMAND_PALETTE_OPENED]: "shell.command-palette",
  [E.COMMAND_PALETTE_USER_SELECTED]: "shell.command-palette",
  [E.DOCS_PAGE_FEEDBACK]: "integrations.docs",
  [E.DOCS_PAGE_FEEDBACK_DETAIL]: "integrations.docs",

  // ── Home ──
  [E.HOME_VIEWED]: "shell.home",
  [E.HOME_NUDGE_CLICKED]: "shell.home",
  [E.HOME_AGENT_LAUNCHED]: "agent.chat",
  [E.HOME_SUGGESTION_CLICKED]: "shell.home",
  [E.HOME_SUGGESTION_DISMISSED]: "shell.home",
});

function isMappedEvent(eventName: string): eventName is AnalyticsEvent {
  return Object.prototype.hasOwnProperty.call(EVENT_FEATURES, eventName);
}

/** A feature id is `<area>.<feature>` by construction (`defineAreaFeatures`). */
export function featureAreaOf(featureId: string): string {
  return featureId.slice(0, featureId.indexOf("."));
}

/**
 * The properties to send for `eventName`: `properties` plus the mapped
 * feature. A caller-supplied `feature_id` is kept, with its own area. Takes
 * any name so the pre-init replay, which holds every catalog, can stamp too;
 * an unmapped name is returned untouched.
 */
export function withEventFeature<Props extends Record<string, unknown> | undefined>(
  eventName: string,
  properties: Props,
): Props | Record<string, unknown> {
  const supplied = properties?.feature_id;
  if (properties && supplied !== undefined) {
    return typeof supplied === "string" && properties.feature_area === undefined
      ? { ...properties, feature_area: featureAreaOf(supplied) }
      : properties;
  }
  if (!isMappedEvent(eventName)) return properties;
  const featureId = EVENT_FEATURES[eventName];
  if (featureId === null) return properties;
  return { ...properties, feature_id: featureId, feature_area: featureAreaOf(featureId) };
}
